/**
 * Repository Event Stream
 * Typed events published by the repository for UI consumption
 */

import * as Stream from 'effect/Stream';
import * as Effect from 'effect/Effect';
import * as Context from 'effect/Context';
import * as Layer from 'effect/Layer';
import * as PubSub from 'effect/PubSub';
import type { TransactionRecord } from '../storage/storage.service';
import type { SyncState } from '../sync/sync-state';

// Repository events as discriminated union
export type RepositoryEvent =
  // Transaction events
  | TransactionCreated
  | TransactionUpdated
  | TransactionDeleted
  | TransactionSynced
  // Sync events
  | SyncStateChanged
  | SyncProgress
  | SyncComplete
  | SyncFailed
  // Conflict events
  | ConflictDetected
  | ConflictResolved;

// Transaction events
export interface TransactionCreated {
  readonly _tag: 'TransactionCreated';
  readonly transaction: TransactionRecord;
  readonly groupId: string;
  readonly timestamp: string;
}

export interface TransactionUpdated {
  readonly _tag: 'TransactionUpdated';
  readonly transaction: TransactionRecord;
  readonly groupId: string;
  readonly timestamp: string;
}

export interface TransactionDeleted {
  readonly _tag: 'TransactionDeleted';
  readonly transactionId: string;
  readonly groupId: string;
  readonly timestamp: string;
}

export interface TransactionSynced {
  readonly _tag: 'TransactionSynced';
  readonly transactionIds: readonly string[];
  readonly groupId: string;
  readonly timestamp: string;
}

// Sync events
export interface SyncStateChanged {
  readonly _tag: 'SyncStateChanged';
  readonly groupId: string;
  readonly oldState: SyncState;
  readonly newState: SyncState;
  readonly timestamp: string;
}

export interface SyncProgress {
  readonly _tag: 'SyncProgress';
  readonly groupId: string;
  readonly pagesFetched: number;
  readonly recordsFetched: number;
  readonly hasMore: boolean;
  readonly timestamp: string;
}

export interface SyncComplete {
  readonly _tag: 'SyncComplete';
  readonly groupId: string;
  readonly recordsSynced: number;
  readonly duration: number;
  readonly timestamp: string;
}

export interface SyncFailed {
  readonly _tag: 'SyncFailed';
  readonly groupId: string;
  readonly error: Error;
  readonly partialProgress: {
    pagesFetched: number;
    recordsSynced: number;
  } | null;
  readonly timestamp: string;
}

// Conflict events
export interface ConflictDetected {
  readonly _tag: 'ConflictDetected';
  readonly groupId: string;
  readonly conflicts: readonly {
    recordId: string;
    recordType: string;
  }[];
  readonly timestamp: string;
}

export interface ConflictResolved {
  readonly _tag: 'ConflictResolved';
  readonly groupId: string;
  readonly resolvedCount: number;
  readonly timestamp: string;
}

// Event service interface
export interface RepositoryEventService {
  readonly publish: (event: RepositoryEvent) => Effect.Effect<void, never>;
  readonly subscribe: () => Stream.Stream<RepositoryEvent, never>;
  readonly subscribeFiltered: (
    filter: (event: RepositoryEvent) => boolean
  ) => Stream.Stream<RepositoryEvent, never>;
  readonly subscribeByGroup: (groupId: string) => Stream.Stream<RepositoryEvent, never>;
}

export class RepositoryEvents extends Context.Tag('RepositoryEvents')<
  RepositoryEvents,
  RepositoryEventService
>() {}

// Live implementation using PubSub
export const RepositoryEventsLive = Layer.effect(
  RepositoryEvents,
  Effect.gen(function* () {
    // Create unbounded PubSub for events
    const pubsub = yield* PubSub.unbounded<RepositoryEvent>();

    return {
      publish: (event) => PubSub.publish(pubsub, event),

      subscribe: () => Stream.fromPubSub(pubsub),

      subscribeFiltered: (filter) =>
        Stream.fromPubSub(pubsub).pipe(
          Stream.filter(filter)
        ),

      subscribeByGroup: (groupId) =>
        Stream.fromPubSub(pubsub).pipe(
          Stream.filter(event => {
            // Filter events by group ID
            switch (event._tag) {
              case 'TransactionCreated':
              case 'TransactionUpdated':
              case 'TransactionDeleted':
              case 'TransactionSynced':
              case 'SyncStateChanged':
              case 'SyncProgress':
              case 'SyncComplete':
              case 'SyncFailed':
              case 'ConflictDetected':
              case 'ConflictResolved':
                return event.groupId === groupId;
              default:
                return false;
            }
          })
        ),
    };
  })
);

// Helper constructors for events
export const Events = {
  transactionCreated: (transaction: TransactionRecord, groupId: string, timestamp: string): TransactionCreated => ({
    _tag: 'TransactionCreated',
    transaction,
    groupId,
    timestamp,
  }),

  transactionUpdated: (transaction: TransactionRecord, groupId: string, timestamp: string): TransactionUpdated => ({
    _tag: 'TransactionUpdated',
    transaction,
    groupId,
    timestamp,
  }),

  transactionDeleted: (transactionId: string, groupId: string, timestamp: string): TransactionDeleted => ({
    _tag: 'TransactionDeleted',
    transactionId,
    groupId,
    timestamp,
  }),

  transactionSynced: (transactionIds: readonly string[], groupId: string, timestamp: string): TransactionSynced => ({
    _tag: 'TransactionSynced',
    transactionIds,
    groupId,
    timestamp,
  }),

  syncStateChanged: (
    groupId: string,
    oldState: SyncState,
    newState: SyncState,
    timestamp: string
  ): SyncStateChanged => ({
    _tag: 'SyncStateChanged',
    groupId,
    oldState,
    newState,
    timestamp,
  }),

  syncProgress: (
    groupId: string,
    pagesFetched: number,
    recordsFetched: number,
    hasMore: boolean,
    timestamp: string
  ): SyncProgress => ({
    _tag: 'SyncProgress',
    groupId,
    pagesFetched,
    recordsFetched,
    hasMore,
    timestamp,
  }),

  syncComplete: (groupId: string, recordsSynced: number, duration: number, timestamp: string): SyncComplete => ({
    _tag: 'SyncComplete',
    groupId,
    recordsSynced,
    duration,
    timestamp,
  }),

  syncFailed: (
    groupId: string,
    error: Error,
    partialProgress: { pagesFetched: number; recordsSynced: number } | null,
    timestamp: string
  ): SyncFailed => ({
    _tag: 'SyncFailed',
    groupId,
    error,
    partialProgress,
    timestamp,
  }),

  conflictDetected: (
    groupId: string,
    conflicts: readonly { recordId: string; recordType: string }[],
    timestamp: string
  ): ConflictDetected => ({
    _tag: 'ConflictDetected',
    groupId,
    conflicts,
    timestamp,
  }),

  conflictResolved: (groupId: string, resolvedCount: number, timestamp: string): ConflictResolved => ({
    _tag: 'ConflictResolved',
    groupId,
    resolvedCount,
    timestamp,
  }),
};
